import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { DescriptionListAccordion } from '~/eds/src/components/Accordion/subcomponents/DescriptionListAccordion';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { useThrottledState } from '~/eds/src/hooks/useThrottledState';
import {
  Field,
  Fields as FieldsType,
} from '~/features/document-handler/types/fields';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { ERROR } from '~/types/toast.types';

import { filterSections } from './Fields.utils';

type Props = {
  handlerId: PilotId;
  documentId?: PilotId;
};

const DEFAULT_TRACKING_DEBOUNCED_MS = 1000;

export const Fields = ({ handlerId, documentId }: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const { data, isFetching, isError, error } =
    api.endpoints.getDocumentInformation.useQuery({
      documentId: handlerId,
      formatDate,
      formatNumber,
    });
  const [throttledSearch, search, setSearch] = useThrottledState(
    '',
    DEFAULT_TRACKING_DEBOUNCED_MS,
  );

  useEffect(() => {
    if (isError) {
      showToast(
        ERROR,
        coerceRtkqError(error)?.response?.data?.detail ||
          intl.formatMessage({
            id: 'Search.Fields.errorLoadingDocument',
            defaultMessage:
              'An error occurred while loading document information.',
            description:
              'Error message when document information fails to load',
          }),
      );
    }
  }, [isError]);

  useEffect(() => {
    if (throttledSearch) {
      trackSegment('searchFields', {
        name: documentId,
        value: throttledSearch,
      });
    }
  }, [throttledSearch]);

  const sections = useMemo(() => {
    if (isError || !data) return [];

    return data.sections.map(toAccordionSection);
  }, [isError, data]);

  const filteredSections = useMemo(() => {
    return filterSections(sections, search);
  }, [sections, search]);

  const handleSearch = (text: Nullable<string>) => {
    setSearch(text ?? '');
  };

  return (
    <ContentContainer loadingContent={{ isLoading: isFetching }}>
      <>
        <Box p={4}>
          <SearchInput
            disabled={isError || isFetching}
            placeholder={intl.formatMessage({
              id: 'Search.Fields.searchFieldsPlaceholder',
              defaultMessage: 'Search fields',
              description: 'Placeholder text for search fields input',
            })}
            name="search"
            onChange={(text) => {
              handleSearch(text);
            }}
            value={search}
          />
        </Box>
        <DescriptionListAccordion
          isEmbedded={true}
          columns={2}
          items={filteredSections}
          search={search}
          onToggle={(isExpanded: boolean, item) => {
            trackSegment('selectAccordion', {
              name: documentId,
              value: item.title,
              isExpanded,
            });
          }}
        />

        {filteredSections.length === 0 && search && (
          <Box mt={2}>
            <Text color="text.quiet">
              {intl.formatMessage({
                id: 'Search.Fields.noMatchingFields',
                defaultMessage: 'No matching fields',
                description: 'Message when no fields match the search query',
              })}
            </Text>
          </Box>
        )}
      </>
    </ContentContainer>
  );
};

export const toAccordionSection = (section: FieldsType['sections'][number]) => {
  return {
    title: section.title,
    terms: section.fields.map(toAccordionTerm),
    isExpanded: true,
  };
};

export const toAccordionTerm = (field: Field) => {
  return {
    title: field.name,
    description: field.displayValue,
    tooltip: field.helpText,
  };
};
